import React, { useContext, useState } from "react";
import {
  Button,
  Form,
  NumberInput,
  Select,
  SelectItem,
  RadioButtonGroup,
  RadioButton,
} from "carbon-components-react";
import { Reset32 } from "@carbon/icons-react";
import { Formik } from "formik";

import {
  DecisionCriteria,
  DecisionCriteriaDisplayValues,
  activeDecisionCriteriaWithBackwardsSupport,
  PropertyTypeDisplayValues,
  SandboxRequest,
  SandboxRequestPercentOrValue,
  parseSandboxRequest,
} from "@eave/shared/types/api";
import { decisionCriteriaMismatchToErrorMsg } from "@eave/shared/utils";
import { isProduction } from "config";

import DollarInput from "web_modules/shared/carbon/DollarInput";
import { makeEvent, normalizeBoolEvent } from "web_modules/shared/FormHelpers/carbon-helpers";

import DollarPercentageInput, { DollarPercentageInputMode } from "../DollarPercentageInput";
import ReosInput from "../ReosInput";
import Status from "../Status";
import Submit from "../Submit";
import { SandboxScenarioContext } from "../../SandboxScenarioProvider";
import { propertyTypeOptions, validationSchema } from "./validation";
import { propertyTypeToDisplay, propertyUsageToDisplay } from "../helpers";

const styles = require("./styles.module.scss");

declare let window: any;

// NOTE: jjw: the helpers below are meant to be called just before sending requests to the backend,
// and resolve SandboxRequestPercentOrValue combo inputs so that only the percent/value field being
// shown in the UI will be populated (the other field will be null) - this helps us be clear about
// which field will drive the calculation input (the backend gives precedence to value if they're
// both populated, but users would expect whichever is being shown in the UI to drive the input)

interface ComboInputModes {
  [inputName: string]: DollarPercentageInputMode;
}

const resolveComboInput = (
  input: SandboxRequestPercentOrValue,
  mode: DollarPercentageInputMode,
): SandboxRequestPercentOrValue => ({
  percent: mode === DollarPercentageInputMode.Percentage ? input.percent : null,
  value: mode === DollarPercentageInputMode.Dollar ? input.value : null,
});

const resolveComboInputs = (
  values: SandboxRequest,
  comboInputModes: ComboInputModes,
): SandboxRequest => ({
  ...values,
  property_taxes: resolveComboInput(values.property_taxes, comboInputModes.property_taxes),
  homeowners_insurance: resolveComboInput(
    values.homeowners_insurance,
    comboInputModes.homeowners_insurance,
  ),
  hoa: resolveComboInput(values.hoa, comboInputModes.hoa),
  supplemental_property_insurance: resolveComboInput(
    values.supplemental_property_insurance,
    comboInputModes.supplemental_property_insurance,
  ),
  mortgage_insurance: resolveComboInput(
    values.mortgage_insurance,
    comboInputModes.mortgage_insurance,
  ),
});

const InputForm = () => {
  const { ownedRealEstate, initialInputs, submitting, error, getInputsForReos, submitScenario } =
    useContext(SandboxScenarioContext);

  const [comboInputModes, setComboInputModes] = useState<ComboInputModes>({});

  const updateInputMode = (inputName: string, mode: DollarPercentageInputMode) => {
    setComboInputModes(prevState => ({
      ...prevState,
      [inputName]: mode,
    }));
  };

  const initialDC = initialInputs.decision_criteria;
  const activeDCs = activeDecisionCriteriaWithBackwardsSupport(initialDC);

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialInputs}
      onSubmit={async (values, formikBag) => {
        formikBag.setStatus(undefined);

        const finalValues = resolveComboInputs(values, comboInputModes);
        const req = parseSandboxRequest(finalValues); // all form values are strings; parse numbers
        const resp = await submitScenario(req);

        if (resp.error) {
          const msg = decisionCriteriaMismatchToErrorMsg(
            resp.error,
            {
              purchasePrice: (req.downpayment + req.loan_amount).toString(),
            },
            propertyTypeToDisplay,
            propertyUsageToDisplay,
          );
          formikBag.setStatus(msg);
        }

        return resp;
      }}
    >
      {({
        status,
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        setValues,
        validateForm,
        values,
      }) => (
        <>
          <div className={styles.status}>
            <Status submitting={submitting} error={!!status || error} message={status} />
          </div>
          <Form
            onKeyDown={e => {
              // HACK: jjw: normally we wouldn't want to prevent this (for good UX), but we need
              // the combo inputs to lose focus before form submission (if they are focused) so that
              // the visible input, if it's blank, gets a zero value - this doesn't affect the
              // calculations, since blank will get coerced to zero, but makes things look better
              // (remove this when we remove the onBlur behavior from DollarPercentageInput)
              if (e.key === "Enter" || e.key === "NumpadEnter") {
                e.preventDefault();
                return false;
              }
            }}
            onSubmit={e => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
              handleSubmit();
            }}
          >
            <Submit disabled={!!Object.keys(errors).length || submitting} />
            <div className={styles.reset}>
              <Button
                kind="tertiary"
                renderIcon={Reset32}
                disabled={submitting}
                onClick={() => {
                  setValues(initialInputs);
                  validateForm();
                }}
              >
                Reset
              </Button>
            </div>
            <div className={styles.inputs}>
              <div className={styles.field}>
                <DollarInput
                  id="loan-amount"
                  name="loan_amount"
                  value={values.loan_amount}
                  labelText="Loan amount"
                  invalid={!!errors.loan_amount}
                  invalidText={errors.loan_amount}
                  disabled={submitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  light
                />
              </div>
              <div className={styles.field}>
                <DollarInput
                  id="downpayment"
                  name="downpayment"
                  value={values.downpayment}
                  labelText="Downpayment"
                  invalid={!!errors.downpayment}
                  invalidText={errors.downpayment}
                  disabled={submitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  light
                />
              </div>
              <div className={styles.field}>
                <DollarInput
                  id="appraised-value"
                  name="appraised_value"
                  value={values.appraised_value}
                  labelText="Appraised value"
                  invalid={!!errors.appraised_value}
                  invalidText={errors.appraised_value}
                  disabled={submitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  light
                />
              </div>
              <div className={styles.field}>
                <Select
                  id="property-type"
                  name="property_type"
                  labelText="Property type"
                  value={values.property_type}
                  invalid={!!errors.property_type}
                  invalidText={errors.property_type}
                  disabled={submitting}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  light
                >
                  {propertyTypeOptions.map(t => (
                    <SelectItem key={t} text={PropertyTypeDisplayValues[t]} value={t} />
                  ))}
                </Select>
              </div>
              <div className={styles.field}>
                <DollarPercentageInput
                  id="property-taxes"
                  name="property_taxes"
                  percentOrValue={values.property_taxes}
                  labelText="Property taxes"
                  errors={errors.property_taxes}
                  disabled={submitting}
                  updateInputMode={updateInputMode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  suffix=" / month"
                  light
                />
              </div>
              <div className={styles.field}>
                <DollarPercentageInput
                  id="homeowners-insurance"
                  name="homeowners_insurance"
                  percentOrValue={values.homeowners_insurance}
                  labelText="Homeowners insurance"
                  errors={errors.homeowners_insurance}
                  disabled={submitting}
                  updateInputMode={updateInputMode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  suffix=" / month"
                  light
                />
              </div>
              <div className={styles.field}>
                <DollarPercentageInput
                  id="hoa"
                  name="hoa"
                  percentOrValue={values.hoa}
                  labelText="HOA"
                  errors={errors.hoa}
                  disabled={submitting}
                  updateInputMode={updateInputMode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  suffix=" / month"
                  light
                />
              </div>
              <div className={styles.field}>
                <DollarPercentageInput
                  id="supplemental-property-insurance"
                  name="supplemental_property_insurance"
                  percentOrValue={values.supplemental_property_insurance}
                  labelText="Supplemental property insurance"
                  errors={errors.supplemental_property_insurance}
                  disabled={submitting}
                  updateInputMode={updateInputMode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  suffix=" / month"
                  light
                />
              </div>
              <div className={styles.field}>
                <DollarPercentageInput
                  id="mortgage-insurance"
                  name="mortgage_insurance"
                  percentOrValue={values.mortgage_insurance}
                  labelText="Mortgage insurance"
                  errors={errors.mortgage_insurance}
                  disabled={submitting}
                  updateInputMode={updateInputMode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  suffix=" / month"
                  light
                />
              </div>
              <div className={styles.field}>
                <NumberInput
                  id="price-exception"
                  name="price_exception"
                  value={values.price_exception}
                  invalid={!!errors.price_exception}
                  invalidText={errors.price_exception}
                  label="Price exception (points)"
                  helperText="use -/+ to adjust eighths of a point"
                  step={0.125}
                  disabled={submitting}
                  onChange={e =>
                    handleChange(makeEvent("price_exception", e.imaginaryTarget.value))
                  }
                  onBlur={handleBlur}
                  light
                />
              </div>
              <div className={styles.field}>
                <Select
                  id="decision-criteria"
                  name="decision_criteria"
                  labelText="Decision criteria"
                  value={values.decision_criteria}
                  invalid={!!errors.decision_criteria}
                  invalidText={errors.decision_criteria}
                  disabled={submitting}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  light
                >
                  {activeDCs.map((dc: DecisionCriteria) => (
                    <SelectItem key={dc} text={DecisionCriteriaDisplayValues[dc]} value={dc} />
                  ))}
                </Select>
              </div>
              <h2>Income</h2>
              <div className={styles.field}>
                <DollarInput
                  id="monthly-income"
                  name="monthly_income"
                  value={values.monthly_income}
                  labelText="Monthly income"
                  invalid={!!errors.monthly_income}
                  invalidText={errors.monthly_income}
                  disabled={submitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  light
                  suffix=" / month"
                />
              </div>
              <h2>Liabilities</h2>
              <div className={styles.field}>
                <DollarInput
                  id="monthly-liabilities"
                  name="monthly_liabilities"
                  value={values.monthly_liabilities}
                  labelText="Monthly liabilities"
                  invalid={!!errors.monthly_liabilities}
                  invalidText={errors.monthly_liabilities}
                  disabled={submitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  light
                  suffix=" / month"
                />
              </div>
              <div className={styles.field}>
                <DollarInput
                  id="total-liabilities-excluded-from-dti"
                  name="total_liabilities_excluded_from_dti"
                  value={values.total_liabilities_excluded_from_dti}
                  labelText="Total liabilities excluded from DTI"
                  invalid={!!errors.total_liabilities_excluded_from_dti}
                  invalidText={errors.total_liabilities_excluded_from_dti}
                  disabled={submitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  light
                />
              </div>
              <h2>Assets</h2>
              <div className={styles.field}>
                <DollarInput
                  id="total-assets"
                  name="total_assets"
                  value={values.total_assets}
                  labelText="Total assets"
                  invalid={!!errors.total_assets}
                  invalidText={errors.total_assets}
                  disabled={submitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  light
                />
              </div>
              <div className={styles.field}>
                <DollarInput
                  id="total-assets-for-reserves"
                  name="total_assets_for_reserves"
                  value={values.total_assets_for_reserves}
                  labelText="Total assets for reserves"
                  invalid={!!errors.total_assets_for_reserves}
                  invalidText={errors.total_assets_for_reserves}
                  disabled={submitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  light
                />
              </div>
              <h2>Real estate owned (REO)</h2>
              <div className={styles.field}>
                <ReosInput
                  ownedRealEstate={ownedRealEstate}
                  values={{
                    reos: values.reos,
                    total_assets: Number(values.total_assets),
                    monthly_liabilities: Number(values.monthly_liabilities),
                    monthly_income: Number(values.monthly_income),
                  }}
                  disabled={submitting}
                  getInputsForReos={getInputsForReos}
                  onUpdate={updatedValues => {
                    setValues({
                      ...values,
                      ...updatedValues,
                    });
                  }}
                />
              </div>
              {values.decision_criteria === DecisionCriteria.Eave20220112VA && (
                <>
                  <h2>VA</h2>
                  <div className={styles.field}>
                    <RadioButtonGroup
                      legendText="Full entitlement?"
                      id="full-entitlement"
                      name="full_entitlement"
                      valueSelected={values.full_entitlement ? "true" : "false"}
                      onChange={e => {
                        const event = makeEvent("full_entitlement", normalizeBoolEvent(e));
                        handleChange(event);
                      }}
                    >
                      <RadioButton value="true" labelText="Yes" />
                      <RadioButton value="false" labelText="No" />
                    </RadioButtonGroup>
                  </div>
                  {!values.full_entitlement && (
                    <div className={styles.field}>
                      <DollarInput
                        id="used-entitlement-amount"
                        name="used_entitlement_amount"
                        value={values.used_entitlement_amount}
                        labelText="Used entitlement amount"
                        invalid={!!errors.used_entitlement_amount}
                        invalidText={errors.used_entitlement_amount}
                        disabled={submitting}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        light
                      />
                    </div>
                  )}
                  <div className={styles.field}>
                    <RadioButtonGroup
                      legendText="Finance VA funding fee?"
                      id="va-finance-funding-fee"
                      name="va_finance_funding_fee"
                      valueSelected={values.va_finance_funding_fee ? "true" : "false"}
                      onChange={e => {
                        const event = makeEvent("va_finance_funding_fee", normalizeBoolEvent(e));
                        handleChange(event);
                      }}
                    >
                      <RadioButton value="true" labelText="Yes" />
                      <RadioButton value="false" labelText="No" />
                    </RadioButtonGroup>
                  </div>
                  {values.va_finance_funding_fee && (
                    <div className={styles.field}>
                      <NumberInput
                        id="va-funding-fee-to-finance"
                        name="va_funding_fee_to_finance"
                        value={values.va_funding_fee_to_finance}
                        invalid={!!errors.va_funding_fee_to_finance}
                        invalidText={errors.va_funding_fee_to_finance}
                        label="VA funding fee % to finance"
                        disabled={submitting}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        light
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <Submit disabled={!!Object.keys(errors).length || submitting} />
          </Form>
        </>
      )}
    </Formik>
  );
};

export default InputForm;
